import { Button, Drawer } from '@toggle/design-system';
import { AssetClassName, ScenariosCondition } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { ScenariosData } from '~/react-query-hooks/scenarios/useScenarios';
import { POSTHOG_TRACKING_SOURCES } from '~/services/tracking/posthog/constants';
import { FilterList } from '~/widgets/scenario/components/filter-list/FilterList';
import { ScenarioPriceMovement } from '~/widgets/scenario/components/scenario-price-movement/ScenarioPriceMovement';
import { ScenarioQuality } from '~/widgets/scenario/components/scenario-quality/ScenarioQuality';

import { MappedConditions } from '../scenario-assistant-modal/use-scenario-assistant/enrichedConditions';
import { ScenarioPastEpisodeSection } from '../scenario-past-episode-section/ScenarioPastEpisodeSection';
import styles from './ScenarioDrawer.module.scss';
import { generateLastConditionLabel } from './utils/utils';

export interface ScenarioDrawerProps {
  activeHorizon: string;
  closeDrawer: () => void;
  horizons: string[];
  isSummary: boolean;
  lastConditions?: ScenariosCondition[] | MappedConditions[];
  scenario: ScenariosData;
  entityTag: string;
}

export const ScenarioDrawer = ({
  activeHorizon,
  closeDrawer,
  horizons,
  isSummary,
  lastConditions = [],
  entityTag,
  scenario,
}: ScenarioDrawerProps) => {
  const { t } = useTranslation(['scenario', 'widget', 'chart']);
  const { navigateToPage } = useNavigateToAssetOverview();

  const isFixedIncome = scenario.entity.asset_class === AssetClassName.Fi;
  const bestHorizon = scenario.report.best_horizon;
  const initHorizon = isSummary ? bestHorizon : activeHorizon;
  const headerTitle = t('scenario:drawer.drawerTitle', {
    entityName: scenario.entity.name,
    count: scenario.episodes.length,
  });

  const filterNames = lastConditions.map(condition => {
    if (!!condition.display) {
      return condition.display;
    } else if (!!(condition as MappedConditions).label) {
      return (condition as MappedConditions).label!;
    } else {
      return generateLastConditionLabel({
        t,
        conditions: [condition as MappedConditions],
      });
    }
  });

  let snakes = lastConditions.map(condition => condition.snake);

  const entitySnakePrice = `${scenario.entity.tag}.price`;

  // Don't display the asset price chart twice
  if (!snakes.some(s => s === entitySnakePrice)) {
    snakes.unshift(entitySnakePrice);
  }

  const navigateToAssetOverview = () => {
    navigateToPage({ entityTag });
  };

  return (
    <Drawer
      className={styles.styledDrawer}
      data-testid="scenario-drawer"
      isOpen
      handleCloseClick={closeDrawer}
      headerButton={
        <Button
          iconName="Expand"
          label={t('widget:assetOverview.assetOverview')}
          onClick={navigateToAssetOverview}
          variant="empty"
          size="xsmall"
          data-testid="asset-overview-button"
        />
      }
    >
      <WithFooterWrapper
        isWrappedComponentOpened
        trackingMetadata={{ source: POSTHOG_TRACKING_SOURCES.SCENARIO_DRAWER }}
      >
        <div className={styles.scenarioDrawerWrapper}>
          <div className={styles.header}>
            <h2 className={styles.title}>{headerTitle}</h2>
            <div className={styles.subtitle}>
              <FilterList list={filterNames} />
            </div>
          </div>
          <ScenarioPriceMovement
            bestHorizon={bestHorizon}
            horizons={horizons}
            isFixedIncome={isFixedIncome}
            scenario={scenario}
          />
          <div className={styles.separator} />
          {scenario.episodes.length > 0 && (
            <ScenarioPastEpisodeSection scenario={scenario} snakes={snakes} />
          )}
          <div className={styles.lineBreak} />
          <ScenarioQuality
            bestHorizon={bestHorizon}
            horizons={horizons}
            initHorizon={initHorizon}
            isFixedIncome={isFixedIncome}
            scenario={scenario}
          />
        </div>
      </WithFooterWrapper>
    </Drawer>
  );
};
